import { useMemo, useState } from "react";
import { RefreshCw, Trash2 } from "lucide-react";
import { ThreeDots } from "react-loader-spinner";

import {
  API_ASSIGN_LEAVE_MANAGER,
  API_LEAVE_MANAGER,
  GET_EMPLOYEE_LIST,
} from "../../../../../../config/Endpoints.js";
import {
  Badge,
  Button,
  Modal,
  ResponsiveDataTable,
  Select,
  type ResponsiveColumn,
} from "../../../../ui/index.ts";
import { EmptyStateMessage } from "../../../../shared/empty-state-message/empty-state-message.tsx";
import { accessToken } from "../../../../../utils/auth.ts";
import { getApi, putApi } from "../../../../../utils/api.ts";
import { showError, showSuccess } from "../../../../../utils/toast.ts";
import {

} from "../../constants.ts";
import type {
  EmployeeOption,
  LeaveManagerDetail,
  ManagerHierarchy,
} from "../../types.ts";
import { extractApiMessage } from "../../utils.ts";
import styles from "./assign-manager-tab.module.css";

interface AssignManagerTabProps {
  employeeOptions: EmployeeOption[];
  loadingEmployees: boolean;
}

type AssignmentRow = {
  id: "reporting" | "leave";
  employeeName: string;
  assignmentType: string;
  currentManager: string;
  currentManagerId: string;
};

export function AssignManagerTab({
  employeeOptions,
  loadingEmployees,
}: AssignManagerTabProps) {
  const [employeeId, setEmployeeId] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [rows, setRows] = useState<AssignmentRow[]>([]);
  const [assignOpen, setAssignOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState<AssignmentRow | null>(null);
  const [managerId, setManagerId] = useState("");

  const selectedEmployeeLabel = useMemo(
    () => employeeOptions.find((option) => option.value === employeeId)?.label ?? "",
    [employeeId, employeeOptions],
  );

  const managerOptions = useMemo(
    () => employeeOptions.filter((option) => option.value !== employeeId),
    [employeeOptions, employeeId],
  );

  const fetchAssignment = async (nextEmployeeId = employeeId) => {
    if (!nextEmployeeId) {
      setRows([]);
      return;
    }

    setLoading(true);
    setRows([]);
    setManagerId("");

    try {
      const [reportingResponse, leaveResponse] = await Promise.all([
        getApi<{ data?: ManagerHierarchy }>(
          `${GET_EMPLOYEE_LIST}/${nextEmployeeId}/manager-hierarchy`,
          accessToken(),
        ),
        getApi<{
          data?: { personalDetail?: LeaveManagerDetail };
        }>(
          `${API_LEAVE_MANAGER}?employeeId=${nextEmployeeId}`,
          accessToken(),
        ),
      ]);

      const reporting = reportingResponse.data?.data ?? {};
      const leave = leaveResponse.data?.data?.personalDetail ?? {};

      setRows([
        {
          id: "reporting",
          employeeName: reporting.name || selectedEmployeeLabel || "-",
          assignmentType: "Reporting Manager",
          currentManager: reporting.manager?.name || "Not Assigned",
          currentManagerId:
            reporting.manager?.id != null
              ? String(reporting.manager.id)
              : "",
        },
        {
          id: "leave",
          employeeName: leave.empName || selectedEmployeeLabel || "-",
          assignmentType: "Leave Manager",
          currentManager: leave.leaveManagerName || "Not Assigned",
          currentManagerId:
            leave.leaveManager != null
              ? String(leave.leaveManager)
              : "",
        },
      ]);
    } catch (e) {
      showError(extractApiMessage(e, "Failed to load manager assignment."));
    } finally {
      setLoading(false);
    }
  };
const handleAssign = async () => {
  if (!employeeId) {
    showError("Please select employee.");
    return;
  }

  if (!managerId) {
    showError("Please select manager.");
    return;
  }

  if (!selectedRow) {
    return;
  }

  setSubmitting(true);

  try {
    if (selectedRow.id === "leave") {
      const { data } = await putApi<{ message?: string }>(
        `${API_ASSIGN_LEAVE_MANAGER}?employeeId=${employeeId}&leaveManagerId=${managerId}`,
        accessToken(),
        {},
      );

      showSuccess(data?.message || "Leave manager assigned successfully.");
    } else {
      await putApi(
        `${GET_EMPLOYEE_LIST}/${employeeId}/manager?newManagerId=${managerId}`,
        accessToken(),
        {},
      );

      showSuccess("Reporting manager assigned successfully.");
    }

    setAssignOpen(false);
    setSelectedRow(null);
    setManagerId("");

    await fetchAssignment(employeeId);
  } catch (e) {
    showError(extractApiMessage(e, "Failed to assign manager."));
  } finally {
    setSubmitting(false);
  }
};

const handleEmployeeChange = (nextEmployeeId: string) => {
  setEmployeeId(nextEmployeeId);
  void fetchAssignment(nextEmployeeId);
};



  const columns = useMemo<ResponsiveColumn<AssignmentRow>[]>(
    () => [
      {
        id: "employeeName",
        header: "Employee Name",
        accessor: "employeeName",
        sortable: true,
      },
      {
        id: "assignmentType",
        header: "Assignment Type",
        accessor: "assignmentType",
        sortable: true,
      },
      {
        id: "currentManager",
        header: "Current Manager",
        sortable: true,
        render: (item) =>
          item.currentManager === "Not Assigned" ? (
            <Badge variant="neutral">Not Assigned</Badge>
          ) : (
            item.currentManager
          ),
      },
      {
        id: "actions",
        header: "Action",
        nowrap: true,
        render: (item) => (
          <Button
            size="sm"
            onClick={() => {
              setSelectedRow(item);
              setManagerId(item.currentManagerId);
              setAssignOpen(true);
            }}
          >
            {item.currentManagerId ? "Reassign" : "Assign"}
          </Button>
        ),
      }
    ],
    [],
  );

  return (
    <div className={styles.stack}>
      <section className={styles.panel}>


        <div className={styles.formGrid}>
          <Select
            id="employee-progression-assign-employee"
            label="Employee"
            value={employeeId}
            onChange={handleEmployeeChange}
            options={employeeOptions}
          placeholder={loadingEmployees ? "Loading..." : "Select Employee Name"}

            searchable
            disabled={loadingEmployees || employeeOptions.length === 0}
          />

        </div>
      </section>

      <section className={styles.panel}>
        <div className={styles.tableHeader}>

          <Button
            variant="muted-outline"
            size="sm"
            startIcon={<RefreshCw size={14} aria-hidden />}
            onClick={() => fetchAssignment()}
            disabled={!employeeId || loading}
          >
            Refresh
          </Button>
        </div>
        {loading ? (
          <div className={styles.loadingWrap}>
            <ThreeDots height="48" width="48" color="var(--color-accent-500)" visible />
          </div>
        ) : (
          <ResponsiveDataTable
            columns={columns}
            data={rows}
            aria-label="Manager Assignments"
            emptyState={
              <EmptyStateMessage message="Select an employee to view assignment details." />
            }
          />
        )}
      </section>
<Modal
  open={assignOpen}
  onClose={() => {
    setAssignOpen(false);
    setManagerId("");
    setSelectedRow(null);
  }}
  title={
    selectedRow?.currentManagerId
      ? `Reassign ${selectedRow?.assignmentType}`
      : `Assign ${selectedRow?.assignmentType}`
  }
  size="md"
  footer={
    <div className={styles.modalFooter}>
      <Button
        variant="light-grey"
        onClick={() => {
          setAssignOpen(false);
          setManagerId("");
          setSelectedRow(null);
        }}
      >
        Cancel
      </Button>

      <Button
        onClick={handleAssign}
        disabled={!managerId || submitting}
      >
        {submitting ? "Saving..." : "Save"}
      </Button>
    </div>
  }
>
  <Select
    id="assign-manager"
    label="Manager"
    value={managerId}
    onChange={setManagerId}
    options={managerOptions}
    placeholder="Select Manager"
    searchable
  />
</Modal>

    </div>
  );
}

